// BinaryMarketsPanel — the cockpit surface for the Arena's binary
// Maniswap engine (I-074 P3b). Self-fetches OPEN binary markets from the
// Arena Supabase (anon-readable per the RLS policy in
// 20260615000000_binary_engine_arena_port.sql) and renders a
// BinaryTradeTicket per market.
//
// Arena-only: the binary engine exists solely on the Arena Supabase
// project; the mount site in MultiPage gates this on IS_GAME_MODE. The
// panel is presentational + read-RPC only and uses self-contained inline
// styles so it does not touch the mobile-jailbreak CSS sharp edges
// (.cockpit-main * carpet-bomb) documented in CLAUDE.md / index.css — it
// renders in normal document flow, no fixed/absolute positioning.
//
// Live behaviour depends on P3a being applied (it is, 2026-06-15) and at
// least one operator-minted OPEN binary market existing (demo-binary-1
// seeded 2026-06-15).

import React, { useCallback, useEffect, useState } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabaseClient';
import BinaryTradeTicket, { BinaryMarket } from './BinaryTradeTicket';

interface Props {
  userId: string;
}

// Columns mirror binary_markets; only what the ticket + preview need.
const SELECT_COLS = 'market_id,question,yes_reserve,no_reserve,p,status';

export default function BinaryMarketsPanel({ userId }: Props) {
  const [markets, setMarkets] = useState<BinaryMarket[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!isSupabaseEnabled() || !supabase) {
      setError('offline');
      return;
    }
    const { data, error: qErr } = await supabase
      .from('binary_markets')
      .select(SELECT_COLS)
      .eq('status', 'OPEN')
      .order('created_at', { ascending: true });
    if (qErr) {
      setError(qErr.message);
      return;
    }
    setError(null);
    // Supabase returns NUMERIC columns as strings — coerce to number so the
    // pricing math (binaryPricing.ts, all float64) operates on numbers.
    setMarkets(
      (data ?? []).map((m): BinaryMarket => ({
        market_id: String(m.market_id),
        question: String(m.question),
        yes_reserve: Number(m.yes_reserve),
        no_reserve: Number(m.no_reserve),
        p: Number(m.p),
        status: String(m.status),
      })),
    );
  }, []);

  useEffect(() => { load(); }, [load]);

  // Nothing to show: no Supabase, a read error, or zero OPEN markets. Stay
  // silent rather than render an empty heading — the binary engine is a
  // held-in-reserve surface and only announces itself when it has markets.
  if (error || !markets || markets.length === 0) return null;

  return (
    <section style={{ marginTop: 16, marginBottom: 24 }}>
      <div style={{
        color: '#F59E0B', fontSize: 9, letterSpacing: '0.22em',
        fontFamily: 'monospace', fontWeight: 700, marginBottom: 10,
      }}>
        BINARY MARKETS · YES / NO
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {markets.map((m) => (
          <BinaryTradeTicket
            key={m.market_id}
            market={m}
            userId={userId}
            onTraded={load}
          />
        ))}
      </div>
    </section>
  );
}
